import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  History,
  NotificationsActive,
  Rule as RuleIcon,
  Send,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  adminAlertsService,
  AdminAlertsError,
  AlertAdmin,
  AlertCategory,
  AlertRule,
  AlertType,
} from '@/services/adminAlertsService';
import { authAPI } from '@/services/authAPI';
import { AlertRuleDialog } from './alerts/AlertRuleDialog';
import { AlertLogTab } from './alerts/AlertLogTab';
import { CATEGORY_LABELS, CATEGORY_ORDER, summariseRecipients } from './alerts/alertLabels';

const PHONE_BANNER_KEY = 'adminAlerts.phoneBannerDismissed';

const readDismissed = () => {
  try {
    return localStorage.getItem(PHONE_BANNER_KEY) === '1';
  } catch {
    return false;
  }
};

export default function AdminAlertsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [types, setTypes] = useState<AlertType[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [admins, setAdmins] = useState<AlertAdmin[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [newRuleType, setNewRuleType] = useState<string | undefined>();

  const [testMenu, setTestMenu] = useState<{ anchor: HTMLElement; rule: AlertRule } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AlertRule | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [showPhoneBanner, setShowPhoneBanner] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [typeData, adminList, ruleList] = await Promise.all([
        adminAlertsService.getTypes(),
        adminAlertsService.getAdmins(),
        adminAlertsService.getRules(),
      ]);
      setTypes(typeData.types || []);
      setRoles(typeData.roles || []);
      setAdmins(adminList || []);
      setRules(ruleList || []);
    } catch (err) {
      if (err instanceof AdminAlertsError && err.status === 403) {
        setForbidden(true);
      } else {
        setLoadError(err instanceof Error ? err.message : 'Could not load admin alerts');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (readDismissed()) return;
    authAPI.getCurrentProfile().then(result => {
      if (result.success && result.user && !result.user.phone) setShowPhoneBanner(true);
    });
  }, []);

  const dismissPhoneBanner = () => {
    setShowPhoneBanner(false);
    try {
      localStorage.setItem(PHONE_BANNER_KEY, '1');
    } catch {
      // ignore storage errors
    }
  };

  const groups = useMemo(() => {
    const typeByKey = new Map(types.map(t => [t.key, t]));
    const categories: (AlertCategory | 'other')[] = [...CATEGORY_ORDER];
    const result = categories.map(category => {
      const categoryTypes = types.filter(t => t.category === category);
      const categoryRules = rules.filter(r => typeByKey.get(r.alertType)?.category === category);
      const unusedTypes = categoryTypes.filter(t => !rules.some(r => r.alertType === t.key));
      return { category, label: CATEGORY_LABELS[category as AlertCategory], rules: categoryRules, unusedTypes, order: categoryTypes };
    });
    const orphanRules = rules.filter(r => !typeByKey.has(r.alertType) || !CATEGORY_ORDER.includes(typeByKey.get(r.alertType)!.category));
    if (orphanRules.length) {
      result.push({ category: 'other', label: 'Other', rules: orphanRules, unusedTypes: [], order: [] });
    }
    return result
      .map(g => ({
        ...g,
        rules: [...g.rules].sort((a, b) => {
          const ai = g.order.findIndex(t => t.key === a.alertType);
          const bi = g.order.findIndex(t => t.key === b.alertType);
          return ai - bi || a.name.localeCompare(b.name);
        }),
      }))
      .filter(g => g.rules.length || g.unusedTypes.length);
  }, [types, rules]);

  const openAdd = (alertType?: string) => {
    setEditingRule(null);
    setNewRuleType(alertType);
    setDialogOpen(true);
  };

  const openEdit = (rule: AlertRule) => {
    setEditingRule(rule);
    setNewRuleType(undefined);
    setDialogOpen(true);
  };

  const handleSaved = (saved: AlertRule) => {
    setRules(prev => {
      const exists = prev.some(r => r.id === saved.id);
      return exists ? prev.map(r => (r.id === saved.id ? saved : r)) : [...prev, saved];
    });
    setDialogOpen(false);
  };

  const handleToggle = async (rule: AlertRule, enabled: boolean) => {
    setTogglingId(rule.id);
    setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, enabled } : r)));
    try {
      const saved = await adminAlertsService.updateRule(rule.id, { enabled });
      setRules(prev => prev.map(r => (r.id === rule.id ? saved : r)));
      toast.success(enabled ? `${rule.name} turned on` : `${rule.name} turned off`);
    } catch (err) {
      setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, enabled: !enabled } : r)));
      toast.error(err instanceof Error ? err.message : 'Could not update the alert');
    } finally {
      setTogglingId(null);
    }
  };

  const handleTest = async (rule: AlertRule, to: 'me' | 'recipients') => {
    setTestMenu(null);
    setTestingId(rule.id);
    try {
      const result = await adminAlertsService.testRule(rule.id, to);
      const message = result.message || `Sent ${result.sent}, failed ${result.failed}`;
      if (result.failed > 0 || result.sent === 0) toast.error(message, { duration: 6000 });
      else toast.success(message);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send the test alert');
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminAlertsService.deleteRule(deleteTarget.id);
      setRules(prev => prev.filter(r => r.id !== deleteTarget.id));
      toast.success('Alert deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the alert');
    } finally {
      setDeleting(false);
    }
  };

  const eventName = (rule: AlertRule) =>
    rule.alertTypeName || types.find(t => t.key === rule.alertType)?.name || rule.alertType;

  const renderRule = (rule: AlertRule) => (
    <Box
      key={rule.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        borderTop: 1,
        borderColor: 'divider',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        opacity: rule.enabled ? 1 : 0.6,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="body1" fontWeight={600}>{rule.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {eventName(rule)}
          {rule.usesDefaultWording === false && ' - own wording'}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ minWidth: 110 }}>
        {rule.sendEmail && <Chip label="Email" size="small" color="primary" variant="outlined" />}
        {rule.sendSms && <Chip label="SMS" size="small" color="secondary" variant="outlined" />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
        {summariseRecipients(rule)}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title={rule.enabled ? 'On' : 'Off'}>
          <Switch
            checked={rule.enabled}
            disabled={togglingId === rule.id}
            onChange={e => handleToggle(rule, e.target.checked)}
          />
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => openEdit(rule)}><Edit fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Send test">
          <span>
            <IconButton
              size="small"
              disabled={testingId === rule.id}
              onClick={e => setTestMenu({ anchor: e.currentTarget, rule })}
            >
              {testingId === rule.id ? <CircularProgress size={18} /> : <Send fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => setDeleteTarget(rule)}><Delete fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  const renderUnused = (type: AlertType) => (
    <Box
      key={type.key}
      sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, px: 2, borderTop: 1, borderColor: 'divider' }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary">{type.name}</Typography>
        <Typography variant="caption" color="text.disabled">{type.description}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">No alert set</Typography>
      <Button size="small" startIcon={<Add />} onClick={() => openAdd(type.key)}>Add</Button>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive /> Admin Alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose who gets an SMS or email when something needs attention, such as payment issues, damage reports,
            payout requests or new listings to approve.
          </Typography>
        </Box>
        {!forbidden && activeTab === 0 && (
          <Button variant="contained" startIcon={<Add />} onClick={() => openAdd()} disabled={loading || !types.length}>
            Add alert
          </Button>
        )}
      </Box>

      {showPhoneBanner && !forbidden && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={() => navigate('/profile')}>Open profile</Button>
              <Button color="inherit" size="small" onClick={dismissPhoneBanner}>Dismiss</Button>
            </Stack>
          }
        >
          Add your phone number in your profile to receive SMS alerts
        </Alert>
      )}

      {forbidden ? (
        <Alert severity="warning">
          Only super admins and admins can manage admin alerts. Ask a super admin if you need an alert set up.
        </Alert>
      ) : (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_e, value) => setActiveTab(value)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Rules" icon={<RuleIcon />} iconPosition="start" />
              <Tab label="Sent alerts" icon={<History />} iconPosition="start" />
            </Tabs>
          </Paper>

          {activeTab === 0 && (
            <>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
              ) : loadError ? (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
                  {loadError}
                </Alert>
              ) : (
                <Stack spacing={3}>
                  {groups.map(group => (
                    <Paper key={group.category} variant="outlined">
                      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold">{group.label}</Typography>
                        <Chip label={`${group.rules.length} ${group.rules.length === 1 ? 'alert' : 'alerts'}`} size="small" />
                      </Box>
                      {group.rules.map(renderRule)}
                      {group.unusedTypes.map(renderUnused)}
                    </Paper>
                  ))}
                </Stack>
              )}
            </>
          )}

          {activeTab === 1 && <AlertLogTab types={types} />}
        </>
      )}

      <Menu anchorEl={testMenu?.anchor} open={!!testMenu} onClose={() => setTestMenu(null)}>
        <MenuItem onClick={() => testMenu && handleTest(testMenu.rule, 'me')}>To me</MenuItem>
        <MenuItem onClick={() => testMenu && handleTest(testMenu.rule, 'recipients')}>To recipients</MenuItem>
      </Menu>

      <AlertRuleDialog
        open={dialogOpen}
        rule={editingRule}
        initialAlertType={newRuleType}
        types={types}
        roles={roles}
        admins={admins}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <Dialog open={!!deleteTarget} onClose={deleting ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Delete alert?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.name} will stop sending. Past entries stay in the sent alerts log.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
